import { useEffect, useMemo, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useAuth } from '../contexts/AuthContext';
import type { IndustryDoc, UserDoc } from '../types';

type Role = 'none' | 'viewer' | 'editor' | 'owner';

export function AdminUsersPage() {
  const { userInfo } = useAuth();
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [industries, setIndustries] = useState<IndustryDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ownableIndustries = useMemo(() => {
    if (!userInfo) return [] as IndustryDoc[];
    if (userInfo.platformAdmin) return industries;
    const owned = new Set(
      userInfo.allowedIndustries.filter((g) => g.role === 'owner').map((g) => g.industryId),
    );
    return industries.filter((ind) => owned.has(ind.id));
  }, [industries, userInfo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [u, inds] = await Promise.all([
          apiCall<UserDoc[]>('/api/users'),
          apiCall<IndustryDoc[]>('/api/industries'),
        ]);
        if (cancelled) return;
        setUsers(u);
        setIndustries(inds);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function changeRole(userId: string, industryId: string, role: Role) {
    setError(null);
    try {
      if (role === 'none') {
        const updated = await apiCall<UserDoc>(
          `/api/users/${userId}/revoke?industryId=${encodeURIComponent(industryId)}`,
          { method: 'POST' },
        );
        setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      } else {
        const updated = await apiCall<UserDoc>(
          `/api/users/${userId}/grant?industryId=${encodeURIComponent(industryId)}`,
          { method: 'POST', body: { role } },
        );
        setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  if (!userInfo) return <p>Loading…</p>;
  const allowed =
    userInfo.platformAdmin || userInfo.allowedIndustries.some((g) => g.role === 'owner');
  if (!allowed) {
    return (
      <div>
        <h1>Users</h1>
        <p style={{ color: 'var(--danger-color)' }}>You don’t have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Users</h1>
      {error && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #fcc', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : users.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>No users yet.</p>
      ) : (
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            background: 'var(--card-bg)',
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ padding: 12 }}>Email</th>
                <th style={{ padding: 12 }}>Name</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Platform admin</th>
                {ownableIndustries.map((ind) => (
                  <th key={ind.id} style={{ padding: 12 }}>
                    {ind.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                  <td style={{ padding: 12, fontWeight: 500 }}>{u.email}</td>
                  <td style={{ padding: 12 }}>{u.displayName || '—'}</td>
                  <td style={{ padding: 12 }}>
                    <span
                      className={`status-pill status-pill--${u.status}`}
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                      }}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td style={{ padding: 12 }}>
                    {u.platformAdmin ? (
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: 'var(--primary-color)',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        Admin
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
                    )}
                  </td>
                  {ownableIndustries.map((ind) => {
                    const role = (u.industryRoles?.[ind.id] ?? 'none') as Role;
                    return (
                      <td key={ind.id} style={{ padding: 12 }}>
                        <select
                          value={role}
                          onChange={(e) => void changeRole(u.id, ind.id, e.target.value as Role)}
                          style={{
                            padding: '4px 6px',
                            border: '1px solid var(--border-color)',
                            borderRadius: 4,
                            fontSize: 13,
                            background: 'var(--card-bg)',
                          }}
                        >
                          <option value="none">none</option>
                          <option value="viewer">viewer</option>
                          <option value="editor">editor</option>
                          <option value="owner">owner</option>
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
